import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { CONFIG } from '@hero/core';
import { SDKConfig } from '../config/sdk-config';
import { AdapterData } from '../adapters/token-adapter';
import { SDKError } from '../errors/sdk-error';
import {
  APILoggingMetadata,
  LoggingMetadataStore,
  logRequestResponse,
  logError,
} from '../utils/logging';

// Single source of truth for header names — see
// `libs/shared/core/src/config/headers.config.ts`. Reading it once
// at module load keeps the interceptor hot path allocation-free.
const H = CONFIG.headers;

export class HttpClient {
  private axiosInstance: AxiosInstance;
  private config: SDKConfig;
  // Store logging metadata separately to avoid polluting request config
  private loggingMetadata: LoggingMetadataStore = new WeakMap();

  /**
   * Auth-v2: single flight de-duplicator for `/auth/refresh`. When
   * `null`, no refresh is in flight; when a Promise, every 401 that
   * arrives while it's pending awaits the same result.
   */
  private inflightRefresh: Promise<string | null> | null = null;

  constructor(config: SDKConfig) {
    this.config = config;
    this.axiosInstance = axios.create({
      baseURL: config.baseURL,
      timeout: config.timeout || 30000,
      // Auth-v2: send cookies on web so `hero_rt` (HttpOnly refresh
      // cookie) travels with the request. Off by default so mobile
      // (RN axios) is unaffected.
      withCredentials: Boolean(config.withCredentials),
      headers: {
        'Content-Type': 'application/json',
        // Gateway convention — every request carries "which app +
        // which platform is calling?" Header names come from the
        // canonical registry in `@hero/core` (`CONFIG.headers`) so
        // there's exactly one source of truth. See
        // `docs/ai/user-signals.md`.
        [H.app]: config.app,
        [H.platform]: config.platform,
      },
    });

    this.setupInterceptors();
  }

  /**
   * Build query string from object
   */
  private buildQueryString(params: Record<string, any>): string {
    // DON't use URLSearchParams here because it will not work properly with nested react native.
    const parts: string[] = [];

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        if (Array.isArray(value)) {
          value.forEach(item => {
            parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(item))}`);
          });
        } else {
          parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
        }
      }
    });

    return parts.length > 0 ? `?${parts.join('&')}` : '';
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      async config => {
        // Get all information from info adapter
        const adapterData = await this.getAdapterData();

        // Header names come from `CONFIG.headers` — the same
        // registry the backend `PresenceInterceptor` / `Tz` / `Lang`
        // decorators read. Change a name in one place and every
        // caller picks it up.
        if (adapterData.access_token) {
          config.headers[H.authorization] = `Bearer ${adapterData.access_token}`;
        }
        if (adapterData.language) {
          config.headers[H.language] = adapterData.language;
        }
        if (adapterData.currentUserLocation) {
          config.headers[H.currentUserLocation] = adapterData.currentUserLocation;
          // Paired freshness + fidelity metadata — the backend's
          // `PresenceInterceptor` uses these to drop stale / coarse
          // reads before writing to the meta fan-out. Legacy SDKs
          // just omit them and the fan-out is skipped (their coord
          // still lands in `ContextService.currentLocation` for the
          // request lifetime).
          if (
            adapterData.currentUserLocationCapturedAt !== undefined &&
            adapterData.currentUserLocationCapturedAt !== null
          ) {
            config.headers[H.currentUserLocationCapturedAt] = String(
              adapterData.currentUserLocationCapturedAt,
            );
          }
          if (
            adapterData.currentUserLocationAccuracyMeters !== undefined &&
            adapterData.currentUserLocationAccuracyMeters !== null
          ) {
            config.headers[H.currentUserLocationAccuracyMeters] = String(
              adapterData.currentUserLocationAccuracyMeters,
            );
          }
        }
        if (adapterData.timezone) {
          config.headers[H.timezone] = adapterData.timezone;
        }

        // Store logging metadata if it exists on the config
        // This happens after the config has been processed by axios
        const loggingMetadata = (config as any).__logging as APILoggingMetadata | undefined;
        if (loggingMetadata) {
          this.loggingMetadata.set(config, loggingMetadata);
        }

        // Apply custom request interceptor if provided
        if (this.config.onRequest) {
          return this.config.onRequest(config);
        }

        return config;
      },
      error => {
        return Promise.reject(error);
      },
    );

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      response => {
        // Log API request and response together if enabled
        if (this.config.enableLogs) {
          try {
            logRequestResponse(response, this.loggingMetadata);
          } catch {
            /* logging must never break the request pipeline */
          }
        }

        // Apply custom response interceptor if provided
        if (this.config.onResponse) {
          return this.config.onResponse(response);
        }
        return response;
      },
      async error => {
        // Log API error if enabled
        if (this.config.enableLogs) {
          try {
            logError(error, this.loggingMetadata);
          } catch {
            /* logging must never break the request pipeline */
          }
        }

        // Auth-v2: silent refresh on 401. Only kicks in when
        // `refreshHandler` is configured and the failing request
        // hasn't already been retried (guarded via `__isRetry`).
        const originalConfig = error.config as
          | (AxiosRequestConfig & { __isRetry?: boolean; url?: string })
          | undefined;
        const status = error.response?.status;
        const canRetry =
          !!this.config.refreshHandler &&
          status === 401 &&
          originalConfig &&
          !originalConfig.__isRetry &&
          // Never try to refresh the refresh endpoint itself — that
          // would recurse forever.
          !this.isRefreshEndpoint(originalConfig.url);

        if (canRetry) {
          try {
            const newAccessToken = await this.runRefresh();
            if (newAccessToken) {
              originalConfig!.__isRetry = true;
              originalConfig!.headers = {
                ...(originalConfig!.headers as any),
                Authorization: `Bearer ${newAccessToken}`,
              };
              return this.axiosInstance.request(originalConfig!);
            }
          } catch {
            /* fall through to the normal error path */
          }
        }

        // Handle errors
        if (this.config.onError) {
          this.config.onError(error);
        }

        // Transform axios error to SDKError
        if (error.response) {
          // Handle blob error responses (shouldn't happen, but be safe)
          const isBlobError = error.response.data instanceof Blob;
          let errorMessage = error.message || 'Request failed';

          if (!isBlobError && error.response.data?.message) {
            errorMessage = error.response.data.message;
          }

          throw new SDKError(
            errorMessage,
            error.response.status,
            !isBlobError ? error.response.data?.code : undefined,
            !isBlobError ? error.response.data : undefined,
          );
        }

        throw new SDKError(error.message || 'Network error');
      },
    );
  }

  private isRefreshEndpoint(url: string | undefined): boolean {
    if (!url) return false;
    // matches `/auth/refresh` and `/api/auth/refresh` regardless of
    // trailing query string.
    return /\/auth\/refresh(\?|$)/.test(url);
  }

  /**
   * Auth-v2: single-flight `/auth/refresh`. Multiple concurrent 401s
   * kick off exactly one refresh call; the rest await the same
   * promise.
   */
  private runRefresh(): Promise<string | null> {
    if (!this.config.refreshHandler) return Promise.resolve(null);
    if (!this.inflightRefresh) {
      this.inflightRefresh = Promise.resolve()
        .then(() => this.config.refreshHandler!())
        .catch(() => null)
        .finally(() => {
          // Clear even on success so the next 401 (post-expiry) can
          // fire a fresh refresh.
          this.inflightRefresh = null;
        });
    }
    return this.inflightRefresh;
  }

  /**
   * Get all adapter data from info adapter
   * Returns default empty values if adapter is not provided
   */
  private async getAdapterData(): Promise<AdapterData> {
    if (!this.config.infoAdapter) {
      return {
        access_token: null,
        language: null,
        currentUserLocation: null,
        timezone: null,
      };
    }

    const data = this.config.infoAdapter();
    return data instanceof Promise ? await data : data;
  }

  async request<T = any>(config: AxiosRequestConfig): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.axiosInstance.request(config);
      return response.data;
    } catch (error) {
      if (error instanceof SDKError) {
        throw error;
      }
      throw new SDKError(error instanceof Error ? error.message : 'Unknown error');
    }
  }

  /**
   * Make an HTTP request with the specified method
   */
  async requestWithMethod<T = any>(
    method: 'GET' | 'POST' | 'PATCH' | 'DELETE',
    url: string,
    data?: any,
    options?: {
      headers?: Record<string, string>;
      timeout?: number;
      __logging?: APILoggingMetadata;
      [key: string]: any;
    },
  ): Promise<T> {
    // Extract logging metadata before stripping it
    const loggingMetadata = options?.__logging;

    // Create request config without __logging (strip it so it never hits the API)
    const { __logging, ...cleanOptions } = options || {};

    const requestConfig: AxiosRequestConfig & { __logging?: APILoggingMetadata } = {
      method,
      url,
      // Temporarily attach logging metadata to config (will be extracted in request interceptor)
      // This ensures we have the same config object reference
      __logging: loggingMetadata,
    };

    // For GET requests, convert data to query params
    if (method === 'GET' && data) {
      const queryString = this.buildQueryString(data as Record<string, any>);
      requestConfig.url = `${url}${queryString}`;
    } else {
      // For other methods, send data in body
      requestConfig.data = data;
    }

    // Merge clean options (headers, timeout, etc.) into request config
    // Note: __logging is already extracted and stored separately, not included in cleanOptions
    if (cleanOptions) {
      if (cleanOptions.headers) {
        requestConfig.headers = {
          ...requestConfig.headers,
          ...cleanOptions.headers,
        };
      }
      if (cleanOptions.timeout !== undefined) {
        requestConfig.timeout = cleanOptions.timeout;
      }
      // Merge any other axios config options (excluding __logging which is already handled)
      Object.keys(cleanOptions).forEach(key => {
        if (key !== 'headers' && key !== 'timeout') {
          (requestConfig as any)[key] = cleanOptions[key];
        }
      });
    }

    return this.request<T>(requestConfig);
  }
}
